import { Link, Text, View } from '@react-pdf/renderer';

import { EventDetailsTable } from './EventDetailsTable';
import { ReachProOutlineLogoPDF } from './ReachProOutlineLogoPDF';
import { styles } from './RetailInvoicePDF.css';
import { RetailInvoicePDFProps } from './RetailInvoicePDF.types';
import {
  formatAmount,
  formatAmountShort,
  formatDate,
} from './RetailInvoicePDF.utils';

// ────────────────────────────────────────────────────────────
// InvoiceHeaderSection
// ────────────────────────────────────────────────────────────

type InvoiceHeaderSectionProps = Pick<
  RetailInvoicePDFProps,
  'status' | 'totalAmount' | 'currency' | 'balanceDue' | 'labels'
>;

export const InvoiceHeaderSection = ({
  status,
  totalAmount,
  currency,
  balanceDue,
  labels,
}: InvoiceHeaderSectionProps) => {
  // POS-8726: badge derived from status + balanceDue. Refunded gets its own red badge.
  const isRefunded = status === 'Refunded';
  const isBalanceDue = !isRefunded && balanceDue > 0;
  const badgeStyle = isRefunded
    ? styles.statusBadgeRefunded
    : isBalanceDue
      ? styles.statusBadgeOutstanding
      : styles.statusBadgePaid;
  const badgeLabel = isRefunded
    ? (labels?.refunded ?? 'Refunded')
    : isBalanceDue
      ? (labels?.balanceDue ?? 'Balance Due')
      : (labels?.paid ?? 'Paid');

  return (
    <View style={styles.headerSection}>
      {/* Logo placeholder */}
      <Text style={styles.invoiceTitle}>
        {labels?.invoiceTitle ?? 'INVOICE'}
      </Text>

      {/* Right: INVOICE title + status + total */}
      <View style={styles.invoiceTitleGroup}>
        <View style={badgeStyle}>
          <Text>{badgeLabel}</Text>
        </View>
        <Text style={styles.totalAmountValue}>
          {formatAmount(totalAmount, currency)}
        </Text>
        <Text style={styles.totalAmountLabel}>
          {labels?.totalInvoiceValue ?? 'Total Invoice Value'}
        </Text>
      </View>
    </View>
  );
};

// ────────────────────────────────────────────────────────────
// InvoiceMetadataRow
// ────────────────────────────────────────────────────────────

type InvoiceMetadataRowProps = Pick<
  RetailInvoicePDFProps,
  'invoiceNumber' | 'invoiceDate' | 'paymentTermsDays' | 'currency' | 'labels'
>;

export const InvoiceMetadataRow = ({
  invoiceNumber,
  invoiceDate,
  paymentTermsDays,
  currency,
  labels,
}: InvoiceMetadataRowProps) => (
  <View style={styles.metadataRow}>
    <View style={styles.metadataColumn}>
      <View style={styles.metadataItem}>
        <Text style={styles.metadataLabel}>
          {labels?.invoiceNumber ?? 'Invoice #'}
        </Text>
        <Text style={styles.metadataValue}>{invoiceNumber}</Text>
      </View>
      <View style={styles.metadataItem}>
        <Text style={styles.metadataLabel}>
          {labels?.invoiceDate ?? 'Invoice Date'}
        </Text>
        <Text style={styles.metadataValue}>{formatDate(invoiceDate)}</Text>
      </View>
    </View>
    <View style={styles.metadataColumn}>
      <View style={styles.metadataItem}>
        <Text style={styles.metadataLabel}>
          {labels?.paymentTerms ?? 'Payment Terms'}
        </Text>
        <Text style={styles.metadataValue}>
          {paymentTermsDays != null
            ? `${paymentTermsDays} days`
            : (labels?.notAvailable ?? 'N/A')}
        </Text>
      </View>
      <View style={styles.metadataItem}>
        <Text style={styles.metadataLabel}>
          {labels?.currency ?? 'Currency'}
        </Text>
        <Text style={styles.metadataValue}>{currency}</Text>
      </View>
    </View>
  </View>
);

// ────────────────────────────────────────────────────────────
// InvoiceAddressSection
// ────────────────────────────────────────────────────────────

type InvoiceAddressSectionProps = Pick<
  RetailInvoicePDFProps,
  | 'brokerName'
  | 'brokerCompany'
  | 'brokerAddress'
  | 'brokerEmail'
  | 'buyerName'
  | 'buyerCompany'
  | 'buyerEmail'
  | 'buyerAddress'
  | 'labels'
>;

export const InvoiceAddressSection = ({
  brokerName,
  brokerCompany,
  brokerAddress,
  brokerEmail,
  buyerName,
  buyerCompany,
  buyerEmail,
  buyerAddress,
  labels,
}: InvoiceAddressSectionProps) => (
  <View style={styles.addressSection}>
    {/* FROM */}
    <View style={styles.addressColumn}>
      <Text style={styles.addressSectionTitle}>{labels?.from ?? 'FROM'}</Text>
      <Text style={styles.addressName}>{brokerName}</Text>
      {brokerCompany ? (
        <Text style={styles.addressDetail}>{brokerCompany}</Text>
      ) : null}
      {brokerAddress ? (
        <Text style={styles.addressDetail}>{brokerAddress}</Text>
      ) : null}
      {brokerEmail ? (
        <Text style={styles.addressDetail}>{brokerEmail}</Text>
      ) : null}
    </View>

    {/* BILLED TO */}
    <View style={styles.addressColumnRight}>
      <Text style={styles.addressSectionTitle}>
        {labels?.billedTo ?? 'BILLED TO'}
      </Text>
      {buyerName ? (
        <Text style={styles.addressName}>{buyerName}</Text>
      ) : (
        <Text style={styles.addressName}>—</Text>
      )}
      {buyerCompany ? (
        <Text style={styles.addressDetail}>{buyerCompany}</Text>
      ) : null}
      {buyerAddress ? (
        <Text style={styles.addressDetail}>{buyerAddress}</Text>
      ) : null}
      {buyerEmail ? (
        <Text style={styles.addressDetail}>{buyerEmail}</Text>
      ) : null}
    </View>
  </View>
);

// ────────────────────────────────────────────────────────────
// InvoiceOrderDetailTable
// ────────────────────────────────────────────────────────────

type InvoiceOrderDetailTableProps = Pick<
  RetailInvoicePDFProps,
  | 'lineItems'
  | 'totalAmount'
  | 'balanceDue'
  | 'balanceDueDate'
  | 'currency'
  | 'status'
  | 'paymentsMade'
  | 'orderDetailRows'
  | 'labels'
>;

export const InvoiceOrderDetailTable = ({
  lineItems,
  balanceDue,
  balanceDueDate,
  currency,
  status,
  paymentsMade,
  orderDetailRows,
  labels,
}: InvoiceOrderDetailTableProps) => {
  // POS-8630: payment-reference items (they carry paymentReferenceId/referenceLabel)
  // are rendered ONLY in the payment section below — exclude them from the Overview
  // table and the Subtotal so the Subtotal equals the amount owed.
  const overviewLineItems = lineItems.filter(
    (item) => item.paymentReferenceId == null && item.referenceLabel == null
  );
  const subtotal = overviewLineItems.reduce(
    (sum, item) => sum + item.amount,
    0
  );
  const computedPaymentsMade = paymentsMade ?? subtotal - balanceDue;
  // POS-8630: Total Proceed = sum of per-event proceeds
  const totalProceed = orderDetailRows.reduce(
    (sum, row) => sum + (row.proceed ?? 0),
    0
  );
  // POS-8630: payment lines to render as purple external links (only entries
  // that actually represent a payment — never plain proceeds/charge rows).
  const paymentLines = lineItems.filter(
    (item) => item.paymentReferenceId != null || item.referenceLabel != null
  );
  const subtotalLines = lineItems.filter(
    (item) => item.paymentReferenceId == null
  );

  const isPaidOrRefunded = status === 'Paid' || status === 'Refunded';
  const rowStyle = isPaidOrRefunded
    ? styles.balanceDueRowPaid
    : styles.balanceDueRow;
  const labelStyle = isPaidOrRefunded
    ? styles.balanceDueLabelPaid
    : styles.balanceDueLabel;
  const dateStyle = isPaidOrRefunded
    ? styles.balanceDueDatePaid
    : styles.balanceDueDate;
  const amountStyle = isPaidOrRefunded
    ? styles.balanceDueAmountPaid
    : styles.balanceDueAmount;

  return (
    <View style={styles.orderDetailSection}>
      {/* Table 2: totals */}
      <View style={[styles.tableContainer, styles.tableContainerWithMarginTop]}>
        {/* Balance Due row */}
        <View style={styles.balanceDueRow}>
          <View style={styles.balanceDueLabelRow}>
            <Text style={labelStyle}>
              {labels?.balanceDue ?? 'Balance Due'}
            </Text>
            {balanceDueDate ? (
              <Text style={dateStyle}> • {formatDate(balanceDueDate)}</Text>
            ) : null}
          </View>
          <Text style={amountStyle}>{formatAmountShort(balanceDue)}</Text>
        </View>
        {/* Subtotal row */}
        <View style={styles.subtotalRow}>
          <Text style={styles.subtotalLabel}>
            {labels?.subtotal ?? 'Subtotal'}
          </Text>
          <View style={styles.subtotalSpacer} />
          <Text style={styles.subtotalAmount}>
            {formatAmountShort(subtotal)}
          </Text>
        </View>
        {/* Total Proceed row — POS-8630: sum of per-event proceeds */}
        {subtotalLines.map((item, index) => {
          return (
            <View key={`pay-${index}`} style={styles.paymentLineRow}>
              <Text
                style={[styles.subtotalLineCell, styles.subtotalLineCellWide]}
              >
                {item.description}
              </Text>
              <Text style={styles.paymentMethodLabel}>
                {item.costType ?? '—'}
              </Text>
              <Text
                style={[styles.paymentLineAmount, styles.paymentLineAmountFlex]}
              >
                {formatAmountShort(item.amount)}
              </Text>
            </View>
          );
        })}
        {/* Payment row */}
        {computedPaymentsMade > 0 ? (
          <View style={[styles.subtotalRow, styles.subtotalRowWithTopBorder]}>
            <Text style={styles.subtotalLabel}>
              {labels?.payment ?? 'Payment'}
            </Text>
            <View style={styles.subtotalSpacer} />
            <Text style={styles.subtotalAmount}>
              {formatAmountShort(computedPaymentsMade)}
            </Text>
          </View>
        ) : null}
        {/* Per-payment-line rows — POS-8630: purple external links.
            Only rendered when at least one lineItem qualifies as a payment. */}
        {paymentLines.map((item, index) => {
          const label =
            item.referenceLabel ??
            (item.paymentReferenceId
              ? `Retail - ${item.paymentReferenceId}`
              : item.description);
          // POS-8726: refund lines (negative amount) get red/pink row styling
          const isRefundLine = item.amount < 0;
          const lineRowStyle = isRefundLine
            ? styles.refundPaymentLineRow
            : styles.paymentLineRow;
          const linkStyle = isRefundLine
            ? styles.refundPaymentLink
            : styles.paymentLink;
          const lineAmountStyle = isRefundLine
            ? styles.refundPaymentLineAmount
            : styles.paymentLineAmount;
          return (
            <View key={`pay-${index}`} style={lineRowStyle}>
              <View style={styles.paymentLinkWrapper}>
                {item.referenceUrl ? (
                  <Link src={item.referenceUrl} style={linkStyle}>
                    {label}
                  </Link>
                ) : (
                  <Text style={linkStyle}>{label}</Text>
                )}
              </View>
              <Text style={styles.paymentMethodLabel}>
                {item.paymentMethodLabel ?? '—'}
              </Text>
              <Text style={[lineAmountStyle, styles.paymentLineAmountFlex]}>
                {formatAmountShort(item.amount)}
              </Text>
            </View>
          );
        })}
      </View>
      {/* ^ end of overview table */}

      <Text style={styles.orderDetailTitle}>
        {labels?.orderDetail ?? 'ORDER DETAIL'}
      </Text>
      <EventDetailsTable orderDetailRows={orderDetailRows} labels={labels} />
    </View>
  );
};

// ────────────────────────────────────────────────────────────
// InvoicePaymentInfoSection
// ────────────────────────────────────────────────────────────

type InvoicePaymentInfoSectionProps = Pick<
  RetailInvoicePDFProps,
  'paymentMethod' | 'paymentReference' | 'labels'
>;

export const InvoicePaymentInfoSection = ({
  paymentMethod,
  paymentReference,
  labels,
}: InvoicePaymentInfoSectionProps) => (
  <View style={styles.paymentInfoSection}>
    <Text style={styles.paymentInfoTitle}>
      {labels?.paymentInfoAndTerms ?? 'PAYMENT INFO & TERMS'}
    </Text>

    {paymentMethod ? (
      <View style={styles.paymentInfoRow}>
        <Text style={styles.paymentInfoLabel}>
          {labels?.paymentMethod ?? 'Payment Method'}
        </Text>
        <Text style={styles.paymentInfoValue}>{paymentMethod}</Text>
      </View>
    ) : null}

    <View style={styles.paymentInfoRow}>
      <Text style={styles.paymentInfoLabel}>
        {labels?.instructions ?? 'Instructions'}
      </Text>
      <Text style={styles.paymentInfoValue}>
        {labels?.payingRemainingBalance ??
          'Paying remaining balance via agreed payment method.'}
      </Text>
    </View>

    {paymentReference ? (
      <View style={styles.paymentInfoRow}>
        <Text style={styles.paymentInfoLabel}>
          {labels?.paymentReference ?? 'Payment Reference'}
        </Text>
        <Text style={styles.paymentInfoValue}>{paymentReference}</Text>
      </View>
    ) : null}

    <View style={styles.paymentInfoRow}>
      <Text style={styles.paymentInfoLabel}>
        {labels?.refundPolicy ?? 'Refund Policy'}
      </Text>
      <Text style={styles.paymentInfoValue}>
        {labels?.allSalesFinal ?? 'All sales are final.'}
      </Text>
    </View>
  </View>
);

// ────────────────────────────────────────────────────────────
// InvoiceBrokerNotesSection
// ────────────────────────────────────────────────────────────

type InvoiceBrokerNotesSectionProps = Pick<
  RetailInvoicePDFProps,
  'brokerNotes' | 'labels'
>;

export const InvoiceBrokerNotesSection = ({
  brokerNotes,
  labels,
}: InvoiceBrokerNotesSectionProps) => {
  if (!brokerNotes) return null;

  return (
    <View style={styles.brokerNotesSection}>
      <Text style={styles.brokerNotesTitle}>
        {labels?.brokerNotes ?? 'BROKER NOTES'}
      </Text>
      <Text style={styles.brokerNotesText}>{brokerNotes}</Text>
    </View>
  );
};

// ────────────────────────────────────────────────────────────
// InvoiceFooter
// ────────────────────────────────────────────────────────────

export const InvoiceFooter = () => (
  <View style={styles.footer}>
    <ReachProOutlineLogoPDF width={24} height={24} color="#221e1f" />
  </View>
);
